// components/tracking/QuickViewPanel.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  MapPin,
  Navigation,
  Map,
  Clock,
  Globe,
  Signal,
  Truck,
  User,
  Phone,
  X,
} from "lucide-react";

const QuickViewPanel = ({ vehicle, mapRef }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    if (vehicle) {
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      setIsVisible(true);
    } else {
      // Delay hiding to prevent flickering
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 150);
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [vehicle]);

  useEffect(() => {
    if (!vehicle || !window.google) return;

    // Calculate position near the cursor
    const handleMouseMove = (e) => {
      const offsetX = 20;
      const offsetY = 20;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const panelWidth = 380;
      const panelHeight = 400;

      let x = e.clientX + offsetX;
      let y = e.clientY + offsetY;

      // Adjust if panel would go off screen
      if (x + panelWidth > windowWidth - 20) {
        x = e.clientX - panelWidth - offsetX;
      }
      if (y + panelHeight > windowHeight - 20) {
        y = windowHeight - panelHeight - 20;
      }
      if (y < 20) {
        y = 20;
      }

      setPosition({ x, y });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [vehicle]);

  if (!vehicle) return null;

  const getStatusConfig = (status) => {
    const configs = {
      active: {
        color: "green",
        label: "Active",
        gradient: "from-green-500 to-emerald-500",
      },
      inactive: {
        color: "red",
        label: "Inactive",
        gradient: "from-red-500 to-rose-500",
      },
      loading: {
        color: "yellow",
        label: "Loading",
        gradient: "from-yellow-500 to-orange-500",
      },
      completed: {
        color: "gray",
        label: "Completed",
        gradient: "from-gray-500 to-slate-500",
      },
      unknown: {
        color: "gray",
        label: "Unknown",
        gradient: "from-gray-500 to-slate-500",
      },
    };
    return configs[status] || configs.unknown;
  };

  const statusConfig = getStatusConfig(vehicle.status);

  const getSignalStrength = (lastUpdate) => {
    const timeDiff = Date.now() - new Date(lastUpdate).getTime();
    if (timeDiff < 300000)
      return {
        label: "Excellent",
        color: "text-green-600",
        bg: "bg-green-100",
      };
    if (timeDiff < 900000)
      return { label: "Good", color: "text-yellow-600", bg: "bg-yellow-100" };
    if (timeDiff < 1800000)
      return { label: "Fair", color: "text-orange-600", bg: "bg-orange-100" };
    return { label: "Poor", color: "text-red-600", bg: "bg-red-100" };
  };

  const signal = getSignalStrength(vehicle.lastUpdate);

  return (
    <div
      ref={panelRef}
      className={`fixed z-[9999] transition-all duration-300 ease-out pointer-events-none ${
        isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        maxWidth: "360px",
      }}
    >
      {/* Backdrop blur */}
      <div className="bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200 overflow-hidden pointer-events-auto">
        {/* Header with gradient */}
        <div
          className={`relative p-4 bg-gradient-to-br ${statusConfig.gradient} text-white`}
        >
          <div className="absolute inset-0 bg-black/10"></div>

          <div className="relative">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{vehicle.number}</h3>
                  <p className="text-white/90 text-xs">
                    {vehicle.vehicle.model}
                  </p>
                </div>
              </div>
              <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                {statusConfig.label}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-1">
                <User className="w-3 h-3" />
                <span className="truncate max-w-[150px]">{vehicle.driver}</span>
              </div>
              <div
                className={`flex items-center space-x-1 px-2 py-0.5 ${signal.bg} ${signal.color} rounded-lg`}
              >
                <Signal className="w-3 h-3" />
                <span className="font-medium">{signal.label}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar">
          {/* Current Location */}
          <div>
            <div className="flex items-start space-x-2">
              <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-0.5">
                  Location
                </p>
                <p className="text-sm text-gray-900 font-medium leading-tight line-clamp-2">
                  {vehicle.position.address}
                </p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <div className="flex items-center space-x-1 mb-0.5">
                <Navigation className="w-3 h-3 text-purple-600" />
                <span className="text-xs font-semibold text-gray-500 uppercase">
                  Speed
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {vehicle.speed.toFixed(1)}
              </p>
              <p className="text-xs text-gray-600">km/h</p>
            </div>

            <div className="p-2 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
              <div className="flex items-center space-x-1 mb-0.5">
                <Map className="w-3 h-3 text-blue-600" />
                <span className="text-xs font-semibold text-gray-500 uppercase">
                  Distance
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {vehicle.route.distance.completed.toFixed(1)}
              </p>
              <p className="text-xs text-gray-600">km</p>
            </div>
          </div>

          {/* Trip Info */}
          <div className="space-y-1.5 p-2 bg-gray-50 rounded-lg text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Trip ID</span>
              <span className="font-semibold text-gray-900 truncate ml-2 max-w-[180px]">
                {vehicle.jobId}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Last Update</span>
              <span className="font-medium text-gray-900">
                {vehicle.lastTrackingTime}
              </span>
            </div>
            {vehicle.stopTime && (
              <div className="flex items-center justify-between p-1.5 bg-red-50 border border-red-200 rounded">
                <span className="text-red-600 font-medium">⏱️ Stopped</span>
                <span className="font-semibold text-red-700">
                  {vehicle.stopTime}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {(vehicle.phone !== "N/A" ||
            (vehicle.trackingLink &&
              vehicle.trackingLink !== "No active trips.")) && (
            <div className="grid grid-cols-2 gap-2">
              {vehicle.phone !== "N/A" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`tel:${vehicle.phone}`);
                  }}
                  className="flex items-center justify-center space-x-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors text-xs"
                >
                  <Phone className="w-3 h-3" />
                  <span>Call</span>
                </button>
              )}
              {vehicle.trackingLink &&
                vehicle.trackingLink !== "No active trips." && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(vehicle.trackingLink, "_blank");
                    }}
                    className="flex items-center justify-center space-x-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-colors text-xs"
                  >
                    <Globe className="w-3 h-3" />
                    <span>Track</span>
                  </button>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickViewPanel;
